import { Injectable } from '@angular/core';
import { AttachmentResponse } from '../../models/attachment/attachment-response';
import { Observable } from 'rxjs';
import { ApiRouteConstants } from '../../../../../../core/constants/api-routes';
import { HttpClient } from '@angular/common/http';
import { AttachmentUpdateRequest } from '../../models/attachment/attachment-update-request';
import { AttachmentCreationRequest } from '../../models/attachment/attachment-creation-request';
import { nameOf } from '../../../../../../core/functions/string-helpers';

@Injectable({
    providedIn: 'root',
})
export class AttachmentService {
    private readonly attachmentApiRoute = ApiRouteConstants.Attachment;

    constructor(private readonly http: HttpClient) {}

    getAll(applicationId: string): Observable<AttachmentResponse[]> {
        const url = this.attachmentApiRoute.getRootUrl(applicationId);

        return this.http.get<AttachmentResponse[]>(url);
    }

    get(applicationId: string, attachmentId: string): Observable<Blob> {
        const url = this.attachmentApiRoute.getRootUrlWithAttachmentId(
            applicationId,
            attachmentId,
        );

        return this.http.get(url, { responseType: 'blob' });
    }

    delete(applicationId: string, attachmentId: string): Observable<void> {
        const url = this.attachmentApiRoute.getRootUrlWithAttachmentId(
            applicationId,
            attachmentId,
        );

        return this.http.delete<void>(url);
    }

    update(
        applicationId: string,
        attachmentId: string,
        request: AttachmentUpdateRequest,
    ): Observable<void> {
        const url = this.attachmentApiRoute.getRootUrlWithAttachmentId(
            applicationId,
            attachmentId,
        );

        return this.http.patch<void>(url, request);
    }

    /**
     * The API endpoint takes a multipart/form-data request so the request object is converted to FormData here.
     */
    create(
        applicationId: string,
        request: AttachmentCreationRequest,
    ): Observable<void> {
        const url = this.attachmentApiRoute.getRootUrl(applicationId);

        const formData = new FormData();

        if (request.file) {
            formData.append(
                nameOf<AttachmentCreationRequest>('file'),
                request.file,
            );
        }

        if (request.attachmentTypeCode) {
            formData.append(
                nameOf<AttachmentCreationRequest>('attachmentTypeCode'),
                request.attachmentTypeCode,
            );
        }

        if (request.description) {
            formData.append(
                nameOf<AttachmentCreationRequest>('description'),
                request.description,
            );
        }

        return this.http.post<void>(url, formData);
    }
}
